'use client';
import { useMemo } from 'react';

import { useCategories } from '@/services/query/landing.queries';
import type { Category } from '@/types/category.types';

import type { PrimaryFilter } from '@/constants';
import { ROUTES } from '@/constants';

const transformCategoryToNavItem = (category: Category): PrimaryFilter => {
  if (!category?.slug || !category?.name) {
    return { label: '', path: '#' };
  }

  const isTrending = category.slug.toLowerCase() === 'trending';
  const isCryptoFinance = ['crypto', 'finance'].includes(category.slug.toLowerCase());

  if (isTrending) {
    return { label: category.name, path: ROUTES.HOME };
  }

  if (isCryptoFinance) {
    const isCrypto = category.slug.toLowerCase() === 'crypto';
    return {
      label: category.name,
      path: isCrypto ? ROUTES.CRYPTO : ROUTES.FINANCE,
    };
  }

  return {
    label: category.name,
    path: ROUTES.CATEGORY(category.slug),
  };
};

export const useNavigationItems = () => {
  const { data, isLoading, error } = useCategories();

  const navItems = useMemo(() => {
    if (isLoading || error || !data) {
      return [];
    }

    // Dynamic items from API
    const dynamicItems = data.map(transformCategoryToNavItem);

    return dynamicItems;
  }, [data, isLoading, error]);

  return {
    navItems,
    isLoading,
    error,
    firstItemSlug: data?.[0]?.slug || 'trending',
  };
};
